import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Button, Card, PageHeader, Badge } from '../../components/ui/UI';
import { ChevronLeft, RefreshCw, Calendar, Tag } from 'lucide-react';
import { format, parseISO, isSameDay } from 'date-fns';
import './ObserverRoomPage.css';

export default function ObserverRoomPage() {
  const { observerId } = useParams();
  const navigate = useNavigate();
  const [observer, setObserver] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [groupedByDate, setGroupedByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedDate, setExpandedDate] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get all observations for this observer
      const { data } = await api.get(`/observations?limit=1000&observer=${observerId}`);
      
      // Assuming we get back observer info and their submissions
      if (data.observations && data.observations.length > 0) {
        // Set observer from first submission
        const firstObs = data.observations[0];
        setObserver({
          id: observerId,
          fullName: firstObs.fullName,
          email: firstObs.email,
          businessUnit: firstObs.businessUnit,
          department: firstObs.department,
          designation: firstObs.designation,
        });
        
        // Group submissions by date
        const grouped = {};
        data.observations.forEach(obs => {
          const date = obs.observationDate ? format(parseISO(obs.observationDate), 'yyyy-MM-dd') : 'No Date';
          if (!grouped[date]) {
            grouped[date] = [];
          }
          grouped[date].push(obs);
        });
        
        // Sort dates in descending order
        const sortedDates = Object.keys(grouped).sort().reverse();
        const sortedGrouped = {};
        sortedDates.forEach(date => {
          sortedGrouped[date] = grouped[date];
        });
        
        setGroupedByDate(sortedGrouped);
        setSubmissions(data.observations);
      }
    } catch (err) {
      toast.error('Failed to load observer room');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [observerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDateExpansion = (date) => {
    setExpandedDate(expandedDate === date ? null : date);
  };

  if (loading) {
    return (
      <div className="fade-in" style={{ padding: 24 }}>
        <div className="skeleton" style={{ height: 100, marginBottom: 20 }} />
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10 }} />)}
      </div>
    );
  }

  if (!observer) {
    return (
      <div className="fade-in">
        <PageHeader title="Observer Room" action={<Button variant="ghost" size="sm" icon={<ChevronLeft size={13} />} onClick={() => navigate('/admin/observations')}>Back</Button>} />
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <p>Observer not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fade-in observer-room-page">
      <PageHeader
        title={`Observer Room: ${observer.fullName}`}
        subtitle={`${submissions.length} submission${submissions.length !== 1 ? 's' : ''}`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="sm" icon={<RefreshCw size={13} />} onClick={fetchData}>Refresh</Button>
            <Button variant="ghost" size="sm" icon={<ChevronLeft size={13} />} onClick={() => navigate('/admin/observations')}>Back</Button>
          </div>
        }
      />

      {/* Observer Info Card */}
      <Card className="observer-info-card">
        <div className="info-grid">
          <div className="info-block">
            <span className="info-label">Full Name</span>
            <span className="info-value">{observer.fullName}</span>
          </div>
          <div className="info-block">
            <span className="info-label">Email</span>
            <span className="info-value">{observer.email}</span>
          </div>
          <div className="info-block">
            <span className="info-label">Business Unit</span>
            <span className="info-value">{observer.businessUnit}</span>
          </div>
          <div className="info-block">
            <span className="info-label">Department</span>
            <span className="info-value">{observer.department}</span>
          </div>
          <div className="info-block">
            <span className="info-label">Designation</span>
            <span className="info-value">{observer.designation}</span>
          </div>
          <div className="info-block">
            <span className="info-label">Total Submissions</span>
            <span className="info-value" style={{ color: 'green', fontWeight: '600' }}>{submissions.length}</span>
          </div>
        </div>
      </Card>

      {/* Submissions Timeline */}
      {submissions.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: '#6b7280' }}>No submissions yet</p>
        </Card>
      ) : (
        <div className="submissions-timeline">
          {Object.entries(groupedByDate).map(([date, dateSubmissions]) => (
            <div key={date} className="timeline-section">
              <div 
                className="timeline-date" 
                onClick={() => toggleDateExpansion(date)}
                style={{ cursor: 'pointer' }}
              >
                <Calendar size={14} style={{ marginRight: 8 }} />
                <span>{format(parseISO(date), 'EEEE, MMMM d, yyyy')}</span>
                <Badge variant="accent" style={{ marginLeft: 12 }}>{dateSubmissions.length} submission{dateSubmissions.length !== 1 ? 's' : ''}</Badge>
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280' }}>
                  {expandedDate === date ? '▼' : '▶'}
                </span>
              </div>
              {expandedDate === date && (
              <div className="submissions-table-wrapper">
                <table className="submissions-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Time</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Location</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dateSubmissions.map((submission, idx) => (
                      <tr 
                        key={submission._id} 
                        className="submission-row-table"
                        onClick={() => navigate(`/admin/observations/${submission._id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="num-cell">#{idx + 1}</td>
                        <td className="time-cell">
                          {submission.createdAt ? format(parseISO(submission.createdAt), 'HH:mm') : submission.shift || 'N/A'}
                        </td>
                        <td className="category-cell">{submission.behaviorCategory || '—'}</td>
                        <td className="type-cell">
                          <Badge variant={submission.behaviorType === 'Safe Behavior' ? 'safe' : 'risk'}>
                            {submission.behaviorType === 'Safe Behavior' ? 'Safe' : 'At-Risk'}
                          </Badge>
                        </td>
                        <td className="location-cell">{submission.observationLocation || '—'}</td>
                        <td className="action-cell">
                          <button 
                            className="view-details-btn"
                            onClick={() => navigate(`/admin/observations/${submission._id}`)}
                          >
                            View Details →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
